import { useMemo } from 'react'
import useReactRouter from 'use-react-router'

export interface UrlFilterConfig {
  /** URL query param name (e.g., 'status') */
  key: string
  /** Optional whitelist of allowed values — invalid values are excluded */
  allowedValues?: string[]
}

/**
 * Reads specified URL query params on mount and returns validated filter values.
 *
 * Read-on-load only — does not sync back to URL on filter change.
 * Designed for cross-page navigation where a source page (e.g., dashboard)
 * links to a list page with pre-applied filters.
 *
 * @example
 * const filters = useUrlFilterParams([
 *   { key: 'status', allowedValues: ['available', 'in-use', 'error'] },
 * ])
 * // URL: ?status=in-use → { status: 'in-use' }
 * // URL: ?status=bogus → {}
 * // URL: (no params)   → {}
 */
// FR-001: Parse URL params matching config keys
// FR-002: Validate against allowedValues
// FR-003: Accept any non-empty value when no allowedValues
// FR-004: Return empty object for no matching params
// FR-005: Return referentially stable (memoized) object
export default function useUrlFilterParams(configs: UrlFilterConfig[]): Record<string, string> {
  const { location } = useReactRouter()

  // Serialize configs for stable dependency comparison — configs are static data
  // (keys + allowed values) that consumers typically pass as inline array literals
  const configsKey = JSON.stringify(configs)

  const result = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const filters: Record<string, string> = {}

    for (const config of configs) {
      const value = params.get(config.key)
      if (!value) continue

      // IF allowedValues is defined, only include if value is in the list
      // ELSE include any non-empty value
      if (config.allowedValues) {
        if (config.allowedValues.includes(value)) {
          filters[config.key] = value
        }
      } else {
        filters[config.key] = value
      }
    }

    return filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, configsKey])

  return result
}
